// pages/reports/DayEndReport.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { BaseTable, BaseColumn } from '@/components/common/table/BaseTable';
import { BaseTableCell } from '@/components/common/table/BaseTableCell';
import { useTableColumns } from '@/hooks/useTableColumns';
import { useUserStore } from '@/store/userStore';
import { reportsApi, ReportParams } from '@/api/endpoints/reports';
import { formatDate } from '@/utils/helpers';
import { ChevronRight, ChevronDown } from 'lucide-react';

interface DayEndReportProps {
  dateFilter: string;
  customDateRange: { from: string; to: string };
}

interface UserGroup {
  userId: string;
  userName: string;
  tasks: any[];
  taskCount: number;
  totalMinutes: number;
}

export const DayEndReport: React.FC<DayEndReportProps> = ({ dateFilter, customDateRange }) => {
  const { users, fetchUsers } = useUserStore();
  const [reportData, setReportData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedUser, setExpandedUser] = useState<string>('');

  // Use centralized columns hook
  const { columns: baseColumns } = useTableColumns('tasks');

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchReport();
  }, [dateFilter, customDateRange]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params: ReportParams = {
        dateType: dateFilter as any,
      };

      if (dateFilter === 'custom' && customDateRange.from && customDateRange.to) {
        params.fromDate = customDateRange.from;
        params.toDate = customDateRange.to;
      }      
      const response = await reportsApi.getDayEndReport(params);      
      setReportData(response.data.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserExpanded = (userId: string) => {
    setExpandedUser(expandedUser === userId ? '' : userId);
  };

  // Mock functions for BaseTableCell compatibility
  const openModal = () => {};
  const handleExistingTaskChange = () => {};

  // Process columns with BaseTableCell renderer
  const columns: BaseColumn[] = useMemo(() => {
    // Filter out actions column for reports
    const reportColumns = baseColumns.filter(col => col.key !== 'actions');
    
    return reportColumns.map((col) => ({
      ...col,
      editable: false, // Reports are read-only
      render: (task: any, index: number) => (
        <BaseTableCell
          task={task}
          column={col}
          index={index}
          isNewTask={false}
          isEditing={false}
          onEditStart={() => {}}
          onEditEnd={() => {}}
          onExistingTaskChange={handleExistingTaskChange}
          clientsList={[]} // Not needed for reports
          users={users}
          openModal={openModal}
          totalTasks={reportData.length}
          serialStart={0}
        />
      ),
    }));
  }, [baseColumns, users, reportData.length]);

  // Group tasks by user
  const userGroups: UserGroup[] = useMemo(() => {
    const groups = new Map<string, UserGroup>();
    
    reportData.forEach(task => {
      const userId = task.allottedTo?._id || task.userId || 'unassigned';
      const userName = task.allottedTo?.name || task.userName || 'Unassigned';
      
      if (!groups.has(userId)) {
        groups.set(userId, {
          userId,
          userName,
          tasks: [],
          taskCount: 0,
          totalMinutes: 0,
        });
      }
      
      const group = groups.get(userId)!;
      group.tasks.push(task);
      group.taskCount++;
      group.totalMinutes += task.tt || 0;
    });
    
    // Sort by user name
    return Array.from(groups.values()).sort((a, b) => a.userName.localeCompare(b.userName));
  }, [reportData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full">   
      {userGroups.map((userGroup) => (
        <div key={userGroup.userId} className="w-full">
          {/* Accordion Header */}
          <div
            className={`
              w-full px-6 py-3 flex items-center justify-between cursor-pointer
              border-b border-gray-200 hover:bg-gray-50 transition-colors
              ${expandedUser === userGroup.userId ? 'bg-gray-50' : 'bg-white'}
            `}
            onClick={() => toggleUserExpanded(userGroup.userId)}
          >
            <div className="flex items-center space-x-3">
              {expandedUser === userGroup.userId ? (
                <ChevronDown size={20} className="text-gray-500" />
              ) : (
                <ChevronRight size={20} className="text-gray-500" />
              )}
              <span className="text-sm font-medium">{userGroup.userName}</span>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <span>{userGroup.taskCount} tasks</span>
              <span>({userGroup.totalMinutes} mins)</span>
            </div>
          </div>

          {/* Expanded Content - BaseTable with fixed height for scrolling */}
          {expandedUser === userGroup.userId && (
            <div className="w-full">
              {/* <div className="bg-yellow-50 p-2 border-b">
                <p className="text-sm text-yellow-800">
                  Showing {userGroup.tasks.length} tasks for {userGroup.userName}
                </p>
              </div> */}
              
              {/* Fixed height container for scrolling */}
              <div className="h-full"> {/* Adjust height as needed */}
                <BaseTable
                  data={userGroup.tasks}
                  columns={columns}
                  loading={false}
                  emptyMessage={`No tasks found for ${userGroup.userName}`}
                  tableClassName="task-table"
                  selectable={false}
                  showFilters={false}
                  sortable={true}
                  onSort={(columnKey) => console.log('Sort by:', columnKey)}
                  dropdownConfig={{}}
                  selectedFilters={{}}
                  getItemId={(item) => item._id || item.id || `task-${Math.random()}`}
                  // Add container class for proper scrolling
                  containerClassName="h-full"
                />
              </div>
            </div>
          )}
        </div>
      ))}

      {userGroups.length === 0 && !loading && (
        <div className="text-center py-10 text-gray-500">
          No tasks found for the selected period
        </div>
      )}
    </div>
  );
};